<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">
          {{ title }}
          <span v-if="!loading && sortedRows.length" class="ml-1 text-xs font-normal text-gray-400">{{ sortedRows.length }}</span>
        </h3>
        <button
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          @click="$emit('refresh')"
        >
          <FontAwesomeIcon :icon="['fas', 'sync']" :class="{ 'animate-spin': loading }" class="text-sm" />
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <div v-else-if="sortedRows.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="icon" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">{{ emptyText }}</p>
    </div>

    <div v-else class="p-4 space-y-2">
      <div
        v-for="(row, i) in sortedRows"
        :key="i"
        class="border border-gray-200 rounded px-2.5 py-1.5"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <div class="text-xs font-medium text-gray-900">
              <span
                v-if="primaryField.tag"
                :class="tagClass(primaryField)"
                :data-demo-blur="primaryField.tag === 'free' ? '' : null"
                :data-spoof-kind="primaryField.tag === 'provider' ? 'provider' : null"
              >{{ fieldValue(row, primaryField) || fallbackTitle }}</span>
              <template v-else>{{ fieldValue(row, primaryField) || fallbackTitle }}</template>
            </div>
            <div class="text-[10px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
              <template v-for="field in detailFields" :key="field.key">
                <span v-if="fieldValue(row, field)">
                  <span class="text-gray-400">{{ field.label }}</span>
                  <span
                    :class="tagClass(field)"
                    :data-demo-blur="field.tag === 'free' ? '' : null"
                    :data-spoof-kind="field.tag === 'provider' ? 'provider' : null"
                  >{{ fieldValue(row, field) }}</span>
                </span>
              </template>
            </div>
          </div>
          <div v-if="dateKey && row[dateKey]" class="text-[10px] text-gray-400 whitespace-nowrap">
            {{ formatDate(row[dateKey]) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Clinical_Record_List',
  components: { FontAwesomeIcon },
  props: {
    title: { type: String, required: true },
    icon: { type: Array, default: () => ['fas', 'file-medical'] },
    rows: { type: Array, default: () => [] },
    fields: { type: Array, required: true },
    dateKey: { type: String, default: '' },
    emptyText: { type: String, default: 'No records found' },
    fallbackTitle: { type: String, default: 'Unknown' },
    loading: { type: Boolean, default: false },
  },
  emits: ['refresh'],
  computed: {
    primaryField() {
      return this.fields[0];
    },
    detailFields() {
      return this.fields.slice(1);
    },
    sortedRows() {
      if (!this.dateKey) return this.rows;
      return [...this.rows].sort((a, b) => String(b[this.dateKey] || '').localeCompare(String(a[this.dateKey] || '')));
    },
  },
  methods: {
    fieldValue(row, field) {
      const keys = Array.isArray(field.key) ? field.key : [field.key];
      for (const k of keys) {
        const v = row[k];
        if (v !== null && v !== undefined && v !== '') return field.format === 'date' ? this.formatDate(v) : v;
      }
      return '';
    },
    tagClass(field) {
      return field.tag ? 'sensitive' : null;
    },
    formatDate(value) {
      if (!value) return '';
      const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>
